import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ACH_CARD_ATTR, parseCardTarget } from "../lib/cardTarget";
import type { CardTarget } from "../model/types";

// Карточка и каталог — отдельным куском: приезжают при первом нажатии.
const AchievementCardDialog = lazy(() => import("./AchievementCardDialog"));

/** Картинка с data-ach-card, на которую нажали (или Enter/пробел на ней), и её цель. */
const hitOf = (target: EventTarget | null): { el: HTMLElement; card: CardTarget } | null => {
    const el = (target as Element | null)?.closest?.(`[${ACH_CARD_ATTR}]`) as HTMLElement | null;
    const card = el ? parseCardTarget(el.getAttribute(ACH_CARD_ATTR)) : null;
    return el && card ? { el, card } : null;
};

/**
 * Нажатие на любую картинку шкалы или достижения — карточка «за что» (PLAN §3.8,
 * R5). Один на весь сайт, рядом с PointsInfoRoot и так же устроен: ловит на
 * документе (в фазе захвата) нажатие и Enter/пробел на всём, что помечено
 * data-ach-card (AchievementPicture). Картинка внутри ссылки или строки
 * открывает карточку, а не ссылку.
 *
 * В основном бандле — только этот слушатель: ни запросов, ни каталога, пока
 * картинку не нажали; а картинки рисуют лишь страницы для администраторов
 * (model/flags.ts), так что у остальных он просто молчит.
 */
export const AchievementCardRoot = () => {
    const [open, setOpen] = useState<CardTarget | null>(null);
    const fromRef = useRef<HTMLElement | null>(null);
    const location = useLocation();

    useEffect(() => {
        const show = (e: Event) => {
            const hit = hitOf(e.target);
            if (!hit) return;
            e.preventDefault();
            e.stopPropagation();
            fromRef.current = hit.el;
            setOpen(hit.card);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") show(e);
        };
        document.addEventListener("click", show, true);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("click", show, true);
            document.removeEventListener("keydown", onKey, true);
        };
    }, []);

    // Переход — карточка закрывается.
    useEffect(() => {
        fromRef.current = null;
        setOpen(null);
    }, [location.pathname, location.search]);

    // Закрыли — фокус возвращается на картинку.
    const close = useCallback(() => {
        const from = fromRef.current;
        fromRef.current = null;
        setOpen(null);
        if (from && from.isConnected) from.focus();
    }, []);

    if (!open) return null;
    return (
        <Suspense fallback={null}>
            <AchievementCardDialog target={open} siteLang={location.pathname.split("/")[1] || ""} onClose={close} />
        </Suspense>
    );
};
